/**
 * The Problem tab — Step 48.
 *
 * The five ITIL sections as STRUCTURED fields, not free text in the thread.
 * The step is explicit about that and it is the right call: *"what is the
 * workaround?"* is a question somebody asks under pressure, and scrolling a
 * conversation for it is how outages get longer.
 *
 * Draft-first, like every other free-text editor here — the auto-commit
 * exception is for single-select properties only.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertOctagon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { RichTextEditor } from '../../components/ui/richtext';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import { cn } from '../../lib/utils';

interface Problem {
  symptoms: string | null;
  rootCause: string | null;
  impact: string | null;
  workaround: string | null;
  permanentSolution: string | null;
  knownError: number;
}

const SECTIONS = [
  { key: 'symptoms' as const, label: 'Symptoms', hint: 'What people see.' },
  { key: 'rootCause' as const, label: 'Root cause', hint: 'Why it happens.' },
  { key: 'impact' as const, label: 'Impact', hint: 'Who it affects.' },
  {
    key: 'workaround' as const,
    label: 'Workaround',
    hint: 'What to do right now.',
  },
  {
    key: 'permanentSolution' as const,
    label: 'Permanent solution',
    hint: 'The fix that ends it.',
  },
];

export function ProblemTab({ id, canEdit }: { id: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: qk.tickets.problem(id),
    queryFn: () => apiFetch<{ entry: Problem | null }>(`/api/tickets/${id}/problem`),
  });

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [known, setKnown] = useState(false);
  /** What the server last agreed to. See the note in ResolutionTab (Step 192):
   *  comparing against the live cache, plus a `setLoaded(false)` on save,
   *  blanked the form from the pre-save row before the refetch landed. */
  const [baseline, setBaseline] = useState<Record<string, string>>({});
  const [knownBase, setKnownBase] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!q.data || loaded) return;
    const e = q.data.entry;
    const initial = {
      symptoms: e?.symptoms ?? '',
      rootCause: e?.rootCause ?? '',
      impact: e?.impact ?? '',
      workaround: e?.workaround ?? '',
      permanentSolution: e?.permanentSolution ?? '',
    };
    setDraft(initial);
    setBaseline(initial);
    setKnown(!!e?.knownError);
    setKnownBase(!!e?.knownError);
    setLoaded(true);
  }, [q.data, loaded]);

  const dirty = loaded && (SECTIONS.some((s) => draft[s.key] !== baseline[s.key]) || known !== knownBase);
  const clearDirty = useDirty(dirty);

  const save = useMutation({
    mutationFn: () => apiSend(`/api/tickets/${id}/problem`, 'PUT', { ...draft, knownError: known }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save.');
      setBaseline(draft);
      setKnownBase(known);
      clearDirty();
      toast.success('Saved.');
      void qc.invalidateQueries({ queryKey: qk.tickets.problem(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
    },
  });

  if (q.isPending)
    return (
      <div className="space-y-3 p-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        {/* A known error is a promise to the desk: the cause is understood and
          there IS a workaround. Prominent, because its whole value is being
          seen before somebody starts diagnosing from scratch. */}
        <label
          className={cn(
            'flex cursor-pointer items-start gap-2 rounded-md border p-3',
            known && 'border-amber-500/40 bg-amber-500/10',
          )}
        >
          <input
            type="checkbox"
            checked={known}
            disabled={!canEdit}
            onChange={(e) => setKnown(e.target.checked)}
            className="mt-0.5 h-4 w-4 accent-[hsl(var(--brand))]"
          />
          <span>
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <AlertOctagon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              Known error
            </span>
            <span className="block text-xs text-muted-foreground">
              The cause is understood and there is a published workaround.
            </span>
          </span>
        </label>

        {SECTIONS.map((s) => (
          <div key={s.key}>
            <label className="mb-1 block text-sm font-semibold">{s.label}</label>
            <p className="mb-1.5 text-xs text-muted-foreground">{s.hint}</p>
            {canEdit ? (
              <RichTextEditor
                value={draft[s.key] ?? ''}
                onChange={(html) => setDraft((d) => ({ ...d, [s.key]: html }))}
              />
            ) : (
              <div
                className="prose prose-sm dark:prose-invert max-w-none overflow-x-auto rounded-md border p-3"
                // Sanitised on WRITE, never on render.
                dangerouslySetInnerHTML={{ __html: draft[s.key] || '<p>—</p>' }}
              />
            )}
          </div>
        ))}
      </div>
      {/* Docked to the bottom of the viewport, like the properties rail's
          Update (Kai). The body above scrolls; this does not, so on a long
          write-up Save is never a scroll away. */}
      {canEdit && (
        <div className="flex shrink-0 items-center gap-2 border-t bg-card px-4 py-2.5">
          <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
        </div>
      )}
    </div>
  );
}
